import { useState, useEffect, useCallback } from 'react';

const sections = [
  { id: 'course-structure', label: 'Course Structure', icon: 'ri-route-line' },
  { id: 'module-breakdown', label: 'Module Details', icon: 'ri-book-open-line' },
  { id: 'learning-outcomes', label: 'Learning Outcomes', icon: 'ri-trophy-line' },
  { id: 'course-features', label: 'Features & Access', icon: 'ri-star-line' },
];

export default function StickyTableOfContents() {
  const [activeSection, setActiveSection] = useState('');
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [progress, setProgress] = useState(0);

  /** 
   * Handles scroll events – calculates visibility, progress and active section.
   * Wrapped in useCallback with an empty dependency array because it only
   * reads static data (sections) and DOM APIs.
   */
  const handleScroll = useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const scrollY = window.scrollY;
    const heroEl = document.getElementById('course-structure');
    const heroHeight = heroEl?.offsetTop ?? 700;
    setVisible(scrollY > heroHeight - 200);

    // Overall page scroll progress (0‑1)
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollProgress = docHeight > 0 ? Math.min(Math.max(scrollY / docHeight, 0), 1) : 0;
    setProgress(scrollProgress);

    // Determine which section is currently at the top of the viewport
    let current = '';
    for (const section of sections) {
      const el = document.getElementById(section.id);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= 160) {
          current = section.id;
        }
      }
    }
    setActiveSection(current);
  }, []);

  // Attach / detach scroll listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    // Run once to initialise state
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  /** Scrolls smoothly to the element with the given id */
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;

    const offset = 100; // space from top
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });

    // Collapse mobile panel after navigation
    setExpanded(false);
  };

  const activeIndex = sections.findIndex((s) => s.id === activeSection);

  // Hide the whole component until the user has scrolled past the hero section
  if (!visible) return null;

  return (
    <>
      {/* Desktop Sidebar – fixed left */}
      <div
        className="hidden xl:flex fixed left-6 top-1/2 -translate-y-1/2 z-40 flex-col items-start gap-1"
        style={{ maxHeight: '70vh' }}
      >
        {/* Progress bar track */}
        <div className="absolute left-[15px] top-[20px] bottom-[20px] w-[2px] bg-[#E8C580]/30 rounded-full">
          <div
            className="w-full bg-gradient-to-b from-[#E48523] to-[#BA3D03] rounded-full transition-all duration-500 ease-out"
            style={{
              height:
                sections.length && activeIndex >= 0
                  ? `${((activeIndex + 1) / sections.length) * 100}%`
                  : '0%',
            }}
          />
        </div>

        {sections.map((section, index) => {
          const isActive = activeSection === section.id;
          const isPast = activeIndex >= 0 && index <= activeIndex;

          return (
            <button
              key={section.id}
              onClick={() => scrollTo(section.id)}
              className="group flex items-center gap-3 py-2 pl-0 pr-3 cursor-pointer relative"
              title={section.label}
            >
              {/* Dot indicator */}
              <div className="relative z-10 flex-shrink-0">
                {/* Pulse ring – only on active */}
                {isActive && (
                  <span className="absolute inset-0 rounded-full animate-ping bg-[#BA3D03]/30 scale-125" />
                )}
                <div
                  className={`relative w-[32px] h-[32px] rounded-full flex items-center justify-center transition-all duration-300 ${
                    isActive
                      ? 'bg-[#BA3D03] shadow-lg shadow-[#BA3D03]/40 scale-110'
                      : isPast
                      ? 'bg-[#E48523]'
                      : 'bg-white border-2 border-[#E8C580]/50 group-hover:border-[#E48523]'
                  }`}
                >
                  <i
                    className={`${section.icon} text-xs ${
                      isActive || isPast
                        ? 'text-white'
                        : 'text-[#3D1203]/40 group-hover:text-[#BA3D03]'
                    } transition-colors`}
                  />
                </div>
              </div>

              {/* Label pill */}
              <span
                className={`text-sm font-medium whitespace-nowrap transition-all duration-300 px-2.5 py-0.5 rounded-full ${
                  isActive
                    ? 'text-[#BA3D03] font-bold bg-[#BA3D03]/10 translate-x-0 opacity-100'
                    : 'text-[#3D1203]/50 bg-transparent group-hover:text-[#3D1203] -translate-x-1 opacity-80 group-hover:opacity-100 group-hover:translate-x-0'
                }`}
              >
                {section.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Tablet / Mobile – floating compact indicator */}
      <div
        className="xl:hidden fixed left-1/2 -translate-x-1/2 z-40 transition-[bottom] duration-400 ease-out"
        style={{ bottom: 'calc(var(--course-sticky-h, 0px) + 1.5rem)' }}
      >
        {/* Expanded panel */}
        {expanded && (
          <>
            <div
              className="fixed inset-0 z-30"
              onClick={() => setExpanded(false)}
            />
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 bg-white rounded-xl shadow-2xl border border-[#E8C580]/30 p-3 min-w-[260px] z-40 animate-slideUp">
              <div className="text-xs font-bold text-[#3D1203]/50 uppercase tracking-wider px-3 pt-1 pb-2 mb-1">
                Jump to Section
              </div>
              {sections.map((section, index) => {
                const isActive = activeSection === section.id;
                const isPast = activeIndex >= 0 && index <= activeIndex;

                return (
                  <button
                    key={section.id}
                    onClick={() => scrollTo(section.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left cursor-pointer transition-all ${
                      isActive
                        ? 'bg-[#BA3D03]/10 text-[#BA3D03]'
                        : 'text-[#3D1203]/70 hover:bg-[#FFF9F5]'
                    }`}
                  >
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 relative ${
                        isActive
                          ? 'bg-[#BA3D03] text-white'
                          : isPast
                          ? 'bg-[#E48523] text-white'
                          : 'bg-[#E8C580]/20 text-[#3D1203]/40'
                      }`}
                    >
                      {isActive && (
                        <span className="absolute inset-0 rounded-full animate-ping bg-[#BA3D03]/30" />
                      )}
                      <i className={`${section.icon} text-xs relative z-10`} />
                    </div>
                    <span className={`text-sm ${isActive ? 'font-bold' : 'font-medium'}`}>
                      {section.label}
                    </span>
                    {/* Step number badge */}
                    <span
                      className={`ml-auto text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                        isActive
                          ? 'bg-[#BA3D03] text-white'
                          : isPast
                          ? 'bg-[#E48523]/20 text-[#E48523]'
                          : 'bg-[#E8C580]/20 text-[#3D1203]/30'
                      }`}
                    >
                      {index + 1}
                    </span>
                  </button>
                );
              })}

              {/* Back to top shortcut */}
              <div className="mt-2 pt-2 border-t border-[#E8C580]/30">
                <button
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    setExpanded(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left cursor-pointer transition-all text-[#3D1203]/50 hover:bg-[#FFF9F5] hover:text-[#BA3D03] group"
                >
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-[#E8C580]/20 group-hover:bg-[#BA3D03]/10 transition-colors">
                    <i className="ri-arrow-up-line text-xs text-[#3D1203]/40 group-hover:text-[#BA3D03] transition-colors" />
                  </div>
                  <span className="text-sm font-medium">Back to top</span>
                  <i className="ri-corner-right-up-line text-xs ml-auto opacity-40 group-hover:opacity-70 transition-opacity" />
                </button>
              </div>
            </div>
          </>
        )}

        {/* Floating pill button */}
        <button
          onClick={() => setExpanded((prev) => !prev)}
          className={`flex items-center gap-2.5 px-4 py-3 rounded-full shadow-xl border transition-all cursor-pointer ${
            expanded
              ? 'bg-[#BA3D03] text-white border-[#BA3D03]'
              : 'bg-white text-[#3D1203] border-[#E8C580]/40 hover:shadow-2xl'
          }`}
        >
          {/* Mini progress ring */}
          <div className="relative w-6 h-6 flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 -rotate-90" viewBox="0 0 24 24">
              <circle
                cx="12"
                cy="12"
                r="10"
                fill="none"
                stroke={expanded ? 'rgba(255,255,255,0.25)' : '#E8C580'}
                strokeWidth="2.5"
              />
              <circle
                cx="12"
                cy="12"
                r="10"
                fill="none"
                stroke={expanded ? '#fff' : '#BA3D03'}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray={`${progress * 62.83} 62.83`}
                className="transition-all duration-300"
              />
            </svg>
          </div>

          {/* Active section name */}
          <span className="text-sm font-semibold whitespace-nowrap">
            {activeIndex >= 0 ? sections[activeIndex].label : 'Contents'}
          </span>

          {/* Step counter badge e.g. "3 / 6" */}
          {activeIndex >= 0 && (
            <span
              className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${
                expanded
                  ? 'bg-white/20 text-white'
                  : 'bg-[#BA3D03]/10 text-[#BA3D03]'
              }`}
            >
              {activeIndex + 1}&nbsp;/&nbsp;{sections.length}
            </span>
          )}

          <i
            className={`ri-arrow-${expanded ? 'down' : 'up'}-s-line text-lg transition-transform`}
          />
        </button>
      </div>

      {/* Simple keyframe animation for the mobile panel */}
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translate(-50%, 8px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
        .animate-slideUp { animation: slideUp 0.2s ease-out; }
      `}</style>
    </>
  );
}
